/**
 * Auto-trim utility to remove white space and transparent edges from images
 * Helps users avoid paying for unused space in their decals
 */

/**
 * Trims white space and transparent pixels from edges of an image
 * @param {string} imageSrc - The source URL of the image to trim
 * @param {number} threshold - RGB threshold for "white" (0-255), default 250
 * @returns {Promise<{src: string, originalWidth: number, originalHeight: number, trimmedWidth: number, trimmedHeight: number, offsetX: number, offsetY: number}>}
 */
export const autoTrimImage = (imageSrc, threshold = 250) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        // Create a canvas to analyze the image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        
        // Find content bounds
        let minX = canvas.width;
        let minY = canvas.height;
        let maxX = 0;
        let maxY = 0;
        
        // Scan all pixels
        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const index = (y * canvas.width + x) * 4;
            const r = pixels[index];
            const g = pixels[index + 1];
            const b = pixels[index + 2];
            const a = pixels[index + 3];
            
            // Check if pixel is NOT transparent and NOT white
            const isTransparent = a < 10; // Almost fully transparent
            const isWhite = r >= threshold && g >= threshold && b >= threshold;
            
            if (!isTransparent && !isWhite) {
              // This pixel has content
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        
        // Check if we found any content
        if (minX > maxX || minY > maxY) {
          // No content found, return original image
          resolve({
            src: imageSrc,
            originalWidth: img.width,
            originalHeight: img.height,
            trimmedWidth: img.width,
            trimmedHeight: img.height,
            offsetX: 0,
            offsetY: 0,
            wasTrimmed: false
          });
          return;
        }
        
        // Add 1px padding to avoid clipping edges
        minX = Math.max(0, minX - 1);
        minY = Math.max(0, minY - 1);
        maxX = Math.min(canvas.width - 1, maxX + 1);
        maxY = Math.min(canvas.height - 1, maxY + 1);
        
        const trimmedWidth = maxX - minX + 1;
        const trimmedHeight = maxY - minY + 1;
        
        // Check if trimming would actually remove significant space (> 5% on any side)
        const leftTrim = minX / canvas.width;
        const rightTrim = (canvas.width - maxX) / canvas.width;
        const topTrim = minY / canvas.height;
        const bottomTrim = (canvas.height - maxY) / canvas.height;
        
        const significantTrim = leftTrim > 0.05 || rightTrim > 0.05 || topTrim > 0.05 || bottomTrim > 0.05;
        
        if (!significantTrim) {
          // Not worth trimming, return original
          resolve({
            src: imageSrc,
            originalWidth: img.width,
            originalHeight: img.height,
            trimmedWidth: img.width,
            trimmedHeight: img.height,
            offsetX: 0,
            offsetY: 0,
            wasTrimmed: false
          });
          return;
        }
        
        // Create new canvas for trimmed image
        const trimmedCanvas = document.createElement('canvas');
        const trimmedCtx = trimmedCanvas.getContext('2d');
        
        trimmedCanvas.width = trimmedWidth;
        trimmedCanvas.height = trimmedHeight;
        
        // Draw the trimmed portion
        trimmedCtx.drawImage(
          canvas,
          minX, minY, trimmedWidth, trimmedHeight, // Source
          0, 0, trimmedWidth, trimmedHeight // Destination
        );
        
        // Convert to blob and create new URL
        trimmedCanvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create trimmed image blob'));
            return;
          }
          
          const trimmedSrc = URL.createObjectURL(blob);
          
          resolve({
            src: trimmedSrc,
            originalWidth: img.width,
            originalHeight: img.height,
            trimmedWidth: trimmedWidth,
            trimmedHeight: trimmedHeight,
            offsetX: minX,
            offsetY: minY,
            wasTrimmed: true,
            percentSaved: Math.round((1 - (trimmedWidth * trimmedHeight) / (img.width * img.height)) * 100)
          });
        }, 'image/png');
        
      } catch (error) {
        console.error('Error trimming image:', error);
        // On error, return original image
        resolve({
          src: imageSrc,
          originalWidth: img.width,
          originalHeight: img.height,
          trimmedWidth: img.width,
          trimmedHeight: img.height,
          offsetX: 0,
          offsetY: 0,
          wasTrimmed: false,
          error: error.message
        });
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image for trimming'));
    };
    
    img.src = imageSrc;
  });
};

